// product-form.js — add to cart (full form + quick-add), variant picker, sticky ATC

// Fly a clone of the product image into the cart icon (Bucks-style).
function flyToCart(btn) {
  try {
    const g = window.gsap;
    const REDUCE = !document.documentElement.classList.contains('force-motion')
      && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!g || REDUCE) return;
    const cartIcon = document.querySelector('[data-cart-open]');
    const card = btn && btn.closest('.product-card');
    const img = (card && card.querySelector('img'))
      || document.querySelector('[data-product-root] img, .product__media img, .product-card img');
    if (!cartIcon || !img) return;
    const r1 = img.getBoundingClientRect();
    const r2 = cartIcon.getBoundingClientRect();
    if (!r1.width) return;
    const clone = img.cloneNode(true);
    clone.removeAttribute('loading');
    Object.assign(clone.style, {
      position: 'fixed', left: r1.left + 'px', top: r1.top + 'px',
      width: r1.width + 'px', height: r1.height + 'px', objectFit: 'cover',
      borderRadius: '14px', zIndex: '100001', pointerEvents: 'none', margin: '0',
      boxShadow: '0 18px 40px rgba(0,0,0,.25)'
    });
    document.body.appendChild(clone);
    g.timeline({ onComplete: () => clone.remove() }).to(clone, {
      duration: 0.85, ease: 'power2.in',
      left: r2.left + r2.width / 2 - 22, top: r2.top + r2.height / 2 - 22,
      width: 44, height: 44, rotation: 28, opacity: 0.35
    });
    g.fromTo(cartIcon, { scale: 1 }, { scale: 1.18, duration: 0.18, yoyo: true, repeat: 1, delay: 0.78 });
  } catch (e) { /* never block add-to-cart on an animation error */ }
}

// Where to write the button's text. Prefer an explicit [data-atc-label] (used
// when the button also contains a price span). Otherwise the visible label is
// the LAST <span> (invert buttons put the bordered bg layer first, label second;
// single-span buttons return their only span).
function labelEl(btn) {
  const explicit = btn.querySelector('[data-atc-label]');
  if (explicit) return explicit;
  const spans = btn.querySelectorAll('span');
  return spans.length ? spans[spans.length - 1] : btn;
}

// Brief "Added ✓" confirmation on the button.
function flashAdded(btn) {
  if (!btn) return;
  const span = labelEl(btn);
  if (btn.__origLabel === undefined) btn.__origLabel = span.textContent;
  span.textContent = 'Added ✓';
  clearTimeout(btn.__addedTimer);
  btn.__addedTimer = setTimeout(() => { span.textContent = btn.__origLabel; }, 1300);
}

// Fastrr's checkout button lives in the cart drawer footer and is hydrated by
// their script, so it can be a moment late after a section refresh. Returns the
// button, or null if this is not a Fastrr (India) session.
function fastrrButton(scope = '.cart-drawer__foot') {
  const el = document.querySelector(`${scope} .sr-headless-checkout, ${scope} .shiprocket-headless button`);
  // Outside India theme.liquid hides every Fastrr mount with CSS, but their
  // button is still in the DOM and still enabled. A hidden button has no client
  // rects: treat it as absent so Buy now goes to the normal checkout.
  return el && !el.disabled && el.getClientRects().length ? el : null;
}
async function waitForFastrr(ms = 2200, scope) {
  const until = performance.now() + ms;
  let el = fastrrButton(scope);
  if (el) return el;
  // Only wait at all when Fastrr is on this page and in use; otherwise return
  // immediately so non-India shoppers are not held up before the native
  // checkout. Outside India (and in India when Fastrr failed to load) the
  // mounts are still in the DOM but theme.liquid hides them with
  // display:none, and waiting for them cost every such Buy now 2.2s.
  const mount = document.querySelector('.shiprocket-headless');
  if (!mount || getComputedStyle(mount).display === 'none') return null;
  while (performance.now() < until) {
    await new Promise((r) => setTimeout(r, 120));
    el = fastrrButton(scope);
    if (el) return el;
  }
  return null;
}

// A failed add used to do nothing at all: no message, the button just went
// back to normal. Shopify answers 422 with a reason ("You can only add 3 of
// this to the cart", "... is already sold out"), and a rate limit comes back
// as an HTML challenge page that is not JSON. Both now say something, next to
// the button that was pressed.
const ADD_FAIL = 'Sorry, we could not add that just now. Please try again in a moment.';
function atcMsgFor(btn) {
  return (btn && btn.closest && (btn.closest('[data-sticky-atc]') || btn.closest('product-form'))?.querySelector('[data-atc-msg]'))
    || (btn && btn.closest && btn.closest('[data-product-root]') ? document.querySelector('product-form [data-atc-msg]') : null);
}
function showAddError(btn, text) {
  const msg = atcMsgFor(btn);
  if (msg) {
    msg.textContent = text || ADD_FAIL;
    msg.hidden = false;
    clearTimeout(msg.__t);
    msg.__t = setTimeout(() => { msg.hidden = true; }, 9000);
    return;
  }
  // Product rails have no message slot: say it on the button, then restore it.
  if (!btn) return;
  const span = labelEl(btn);
  if (btn.__origLabel === undefined) btn.__origLabel = span.textContent;
  span.textContent = 'Not added, try again';
  clearTimeout(btn.__addedTimer);
  btn.__addedTimer = setTimeout(() => { span.textContent = btn.__origLabel; }, 2600);
}
function hideAddError(btn) {
  const msg = atcMsgFor(btn);
  if (msg && !msg.hidden) { msg.hidden = true; clearTimeout(msg.__t); }
}
// Reads a cart response without trusting it to be JSON (a 429 challenge is HTML).
async function cartJson(r) {
  const text = await r.text();
  let data = null;
  try { data = JSON.parse(text); } catch (e) { data = null; }
  if (!r.ok || !data || data.status) {
    const why = data && (data.description || data.message);
    return { error: typeof why === 'string' && why && why !== 'Cart Error' ? why : ADD_FAIL, data };
  }
  return { data };
}

// Resolves once the free-gift engine (snippets/free-gift-ladder.liquid) has
// put the gift this cart earns INTO the cart. Every way out to checkout waits
// on it: without it Fastrr read /cart.js ~120ms after the add, the engine
// wrote the gift ~0.5-1.5s later, and the order left without its gift.
function giftsReady() {
  return typeof window.taabaaGiftsReady === 'function' ? window.taabaaGiftsReady(5000) : Promise.resolve();
}

// ---------------------------------------------------------------------------
// Buy now buys ONLY what is on the page, with the gift THAT earns.
//
// The free gift is worked out on the cart and Fastrr's own buy-now path skips
// the cart, so Buy now goes through the cart (see the Fastrr mount in
// main-product.liquid). Checking out the whole cart, though, gave a Buy now
// order the gift the WHOLE cart earned and billed everything else in it too.
// Merchant, 2026-09-29: "If I buy now and only 1 gift is according to that
// then that free gift should be added."
//
// So for the length of a Buy now the rest of the cart is set aside: its lines
// (and the cart's gift details and note) are saved on the device, in
// localStorage so a redirect or a closed tab cannot lose them; the cart is
// emptied, this product goes in, the gift engine adds the gift this product
// alone earns, and checkout reads that. The rest comes back when the shopper
// closes the checkout, or on the next page after an order.
//
// Rules from the code review of the first version, each handled below:
//  - Every tab sees the saved cart, so only the tab running the checkout puts
//    it back (or any tab, once that one has been silent for 2 minutes). A
//    second tab putting it back while the checkout was open let the order's
//    clear-cart wipe it.
//  - Putting back never empties the cart wholesale: it takes out what the Buy
//    now put in and returns the rest, so anything added since survives.
//  - A Buy now line that is gone by then was bought (Fastrr and Shopify both
//    empty the cart after an order), so it is not handed back a second time.
//  - One cart operation at a time, across tabs (Web Locks), and a put-back
//    still scheduled from an earlier Buy now is cancelled by the next one.
// ---------------------------------------------------------------------------
const STASH_KEY = 'tb_buynow_cart';
const OWNER_KEY = 'tb_buynow_owner';
const STASH_TTL = 14 * 864e5; // a Shopify cart lives about two weeks
const STALE_MS = 120000;      // another tab may put it back after 2 minutes of silence
const GIFT_ATTRS = ['Is this a gift', 'Recipient', 'Occasion', 'Occasion date', 'Gift message'];
const cartRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function readStash() {
  try {
    const s = JSON.parse(localStorage.getItem(STASH_KEY) || 'null');
    if (s && Array.isArray(s.items) && Date.now() - s.at < STASH_TTL) return s;
    localStorage.removeItem(STASH_KEY);
  } catch (e) { /* storage blocked */ }
  return null;
}
function writeStash(s) {
  try { localStorage.setItem(STASH_KEY, JSON.stringify(s)); return !!readStash(); } catch (e) { return false; }
}
function dropStash() {
  try { localStorage.removeItem(STASH_KEY); } catch (e) { /* storage blocked */ }
  stopBeat();
}

// The tab running this Buy now's checkout. sessionStorage belongs to one tab
// and survives that tab's own redirects (payment pages, the thank-you page).
// While it lives it says so every 5s; the stash is anyone's once it goes quiet.
let beatTimer = null;
function stopBeat() { clearInterval(beatTimer); beatTimer = null; }
function ownsStash(st) {
  try { return !!st && !!st.id && sessionStorage.getItem(OWNER_KEY) === st.id; } catch (e) { return false; }
}
function startBeat() {
  stopBeat();
  beatTimer = setInterval(() => {
    const st = readStash();
    if (!st || !ownsStash(st)) { stopBeat(); return; }
    st.beat = Date.now();
    writeStash(st);
  }, 5000);
}
function claimStash(st) {
  try { sessionStorage.setItem(OWNER_KEY, st.id); } catch (e) { /* the 2-minute rule still applies */ }
  startBeat();
}

// Lines compared the way the cart merges them: same variant, same properties.
function normProps(p) {
  return JSON.stringify(Object.keys(p || {})
    .filter((k) => String(p[k]).trim() !== '')
    .sort()
    .map((k) => [k, String(p[k])]));
}
const sig = (id, props) => String(id) + '|' + normProps(props);
function countLines(lines, updates) {
  const n = {};
  (lines || []).forEach((l) => {
    const q = updates && Object.prototype.hasOwnProperty.call(updates, l.key) ? updates[l.key] : l.quantity;
    const k = sig(l.variant_id, l.properties);
    n[k] = (n[k] || 0) + q;
  });
  return n;
}
// a minus b, never below zero.
function minusLines(a, b) {
  const left = {};
  (b || []).forEach((x) => { const k = sig(x.id, x.properties); left[k] = (left[k] || 0) + x.quantity; });
  return (a || []).map((x) => {
    const k = sig(x.id, x.properties);
    const take = Math.min(left[k] || 0, x.quantity);
    left[k] = (left[k] || 0) - take;
    return Object.assign({}, x, { quantity: x.quantity - take });
  }).filter((x) => x.quantity > 0);
}
function plusLines(a, b) {
  const out = (a || []).map((x) => Object.assign({}, x));
  (b || []).forEach((x) => {
    const hit = out.find((y) => sig(y.id, y.properties) === sig(x.id, x.properties));
    if (hit) hit.quantity += x.quantity; else out.push(Object.assign({}, x));
  });
  return out;
}

// The ₹0 gift lines belong to the gift engine, which adds and removes them
// itself. Everything else, a gift that reverted to paid included, is the
// shopper's own cart.
function giftTwinIds() {
  try { return JSON.parse(document.querySelector('[data-gift-ladder]')?.getAttribute('data-known') || '[]').map(String); }
  catch (e) { return []; }
}
const isTwin = (l, twins) => !!(l.properties && l.properties._free_gift) || twins.indexOf(String(l.variant_id)) >= 0;
function shopperLines(cart) {
  const twins = giftTwinIds();
  return (cart.items || [])
    .filter((l) => !isTwin(l, twins))
    .map((l) => {
      const it = { id: l.variant_id, quantity: l.quantity };
      if (l.properties && Object.keys(l.properties).length) it.properties = l.properties;
      return it;
    });
}

// GET when there is no body. Never throws: { data } or { error }, plus status.
async function cartCall(path, body) {
  try {
    const r = await fetch(cartRoot() + path, body === undefined
      ? { headers: { Accept: 'application/json' } }
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const res = await cartJson(r);
    res.status = r.status;
    return res;
  } catch (e) { return { error: ADD_FAIL, status: 0 }; }
}
// The drawer, the header count and, on /cart, the page itself.
function cartSections() {
  const sid = window.taabaaCartPageSectionId?.() || '';
  return 'cart-drawer,header' + (sid ? ',' + sid : '');
}
function paint(sections) {
  if (!sections) { document.dispatchEvent(new CustomEvent('cart:refresh')); return; }
  window.refreshCartSections?.(sections);
  const sid = window.taabaaCartPageSectionId?.() || '';
  if (sid && sections[sid]) window.taabaaRepaintCartPage?.(sections[sid]);
}

// One cart operation at a time, in this tab and across tabs.
let cartChain = Promise.resolve();
function withCartLock(fn) {
  if (navigator.locks && navigator.locks.request) return navigator.locks.request('tb-buynow-cart', fn);
  const run = cartChain.then(fn, fn);
  cartChain = run.catch(() => {});
  return run;
}

// Put the cart back: take out what the Buy now put in, return what was set
// aside. Callers hold the lock (restoreCart, or soloCart when its add fails).
async function restoreNow() {
  const st = readStash();
  if (!st) return;
  const cart = await cartCall('cart.js');
  if (cart.error) return; // still saved; the next page tries again
  const twins = giftTwinIds();
  const solo = st.solo || [];
  const sections = cartSections();

  // What the Buy now put in, as far as it is still here. The gift twins go
  // too; the gift engine adds the right one for the cart that comes back.
  const left = {};
  solo.forEach((x) => { const k = sig(x.id, x.properties); left[k] = (left[k] || 0) + x.quantity; });
  const updates = {};
  cart.data.items.forEach((l) => {
    if (isTwin(l, twins)) { updates[l.key] = 0; return; }
    const k = sig(l.variant_id, l.properties);
    const take = Math.min(left[k] || 0, l.quantity);
    if (take) { updates[l.key] = l.quantity - take; left[k] -= take; }
  });
  // Gone already means bought: the same pieces come off what goes back, so a
  // cart that held them before the Buy now does not offer them a second time.
  const bought = !!st.ordered || Object.keys(left).some((k) => left[k] > 0);
  const back = bought ? minusLines(st.items, solo) : st.items.map((x) => Object.assign({}, x));

  let data = null;
  if (Object.keys(updates).length) {
    const u = await cartCall('cart/update.js', { updates, sections });
    if (u.error) return; // nothing has moved yet; tried again later
    data = u.data;
  }
  const pending = [];
  if (back.length) {
    const all = await cartCall('cart/add.js', { items: back, sections });
    if (!all.error) data = all.data;
    else {
      // One line that can no longer be bought (sold out since) fails the whole
      // batch. Put the rest back one at a time, counting from what is really
      // in the cart now in case part of the batch went in.
      const was = countLines(cart.data.items, updates);
      const now = await cartCall('cart.js');
      const got = now.error ? was : countLines(now.data.items);
      for (const it of back) {
        const k = sig(it.id, it.properties);
        const need = it.quantity - Math.max(0, (got[k] || 0) - (was[k] || 0));
        if (need <= 0) continue;
        const one = await cartCall('cart/add.js', { items: [Object.assign({}, it, { quantity: need })], sections });
        if (!one.error) data = one.data;
        else if (one.status !== 422) pending.push(Object.assign({}, it, { quantity: need })); // network or rate limit
      }
    }
  }
  // The cart's gift details and note, set aside with it.
  if (st.blanked) {
    const body = { attributes: st.attrs || {} };
    if (st.note) body.note = st.note;
    await cartCall('cart/update.js', body);
  }
  // Whatever failed for a reason that can pass is kept for the next page,
  // and only that, so the retry cannot hand anything back twice.
  if (pending.length) writeStash(Object.assign({}, st, { items: pending, solo: [], ordered: false, blanked: false }));
  else dropStash();
  paint(data && data.sections);
  await giftsReady();
}
let restoring = null;
function restoreCart() {
  if (restoring) return restoring;
  restoring = withCartLock(restoreNow).catch(() => {}).finally(() => { restoring = null; });
  return restoring;
}

// Empty the cart down to this Buy now and let the gift engine add the gift it
// earns. Resolves { ok } or { error }.
async function soloCart(items) {
  // A put-back still scheduled from an earlier Buy now must not land in the
  // middle of this one, and the gift engine must not be halfway through a
  // write to the old cart when it is emptied.
  stopWatch();
  if (window.taabaaGiftsPending && window.taabaaGiftsPending()) await giftsReady();
  return withCartLock(async () => {
    const cart = await cartCall('cart.js');
    if (cart.error) return cart;
    const lines = shopperLines(cart.data);
    const attrs = {};
    GIFT_ATTRS.forEach((k) => { const v = cart.data.attributes && cart.data.attributes[k]; if (v) attrs[k] = v; });
    const note = cart.data.note || '';
    const prior = readStash();
    let st;
    if (prior) {
      // An earlier Buy now's set-aside cart is the real one. Its one-product
      // cart may still be here (never put back, e.g. after a payment page):
      // that goes, and anything added since joins what is set aside.
      st = Object.assign({}, prior, { items: plusLines(prior.items, minusLines(lines, prior.solo || [])), solo: items, ordered: false, beat: Date.now() });
    } else {
      st = { id: Date.now().toString(36) + Math.random().toString(36).slice(2, 8), at: Date.now(), beat: Date.now(), items: lines, solo: items, attrs, note, blanked: false };
    }
    const blank = Object.keys(attrs).length > 0 || !!note;
    if (blank) { st.attrs = Object.assign({}, st.attrs, attrs); if (note) st.note = note; st.blanked = true; }
    const saved = writeStash(st);
    if (!saved && lines.length) {
      // Nowhere safe to keep the cart (storage blocked): leave it alone and
      // check all of it out rather than risk losing any of it.
      const add = await cartCall('cart/add.js', { items, sections: cartSections() });
      if (add.error) return add;
      paint(add.data.sections);
      await giftsReady();
      return { ok: true };
    }
    if (saved) claimStash(st);
    if (cart.data.items.length) {
      const cleared = await cartCall('cart/clear.js', {});
      if (cleared.error) { if (!prior) dropStash(); return cleared; }
    }
    // Gift details and the note belong to the cart, not to this order.
    if (saved && blank) {
      const off = {};
      Object.keys(attrs).forEach((k) => { off[k] = ''; });
      await cartCall('cart/update.js', { attributes: off, note: '' });
    }
    const add = await cartCall('cart/add.js', { items, sections: cartSections() });
    if (add.error) {
      // Nothing went in, so nothing was bought: put everything back.
      const now = readStash();
      if (now) { writeStash(Object.assign({}, now, { solo: [] })); await restoreNow(); }
      return add;
    }
    paint(add.data.sections);
    await giftsReady();
    return { ok: true };
  });
}

// Watch the checkout a Buy now opened and put the cart back once it is done
// with: when the shopper closes it, and after an order (Fastrr posts a
// `clearCart` action, empties the cart, then goes to the thank-you page; the
// next page does it, or this one if it stays).
let stopActiveWatch = null;
function stopWatch() { if (stopActiveWatch) stopActiveWatch(); }
function watchSoloCheckout() {
  stopWatch();
  if (!readStash()) return;
  const BOX = 'fastrr-main-container';
  let opened = false;
  let ordered = false;
  let timer = null;
  let never = null;
  let tries = 0;
  let mo = null;
  const stop = () => {
    mo?.disconnect();
    window.removeEventListener('message', onMsg);
    clearTimeout(timer);
    clearTimeout(never);
    if (stopActiveWatch === stop) stopActiveWatch = null;
  };
  const later = (ms) => { clearTimeout(timer); timer = setTimeout(() => { stop(); restoreCart(); }, ms); };
  function onMsg(e) {
    const acts = e && e.data && Array.isArray(e.data.actions) ? e.data.actions : [];
    if (!acts.some((a) => a && a.action === 'clearCart')) return;
    ordered = true;
    const st = readStash();
    if (st) { st.ordered = true; writeStash(st); }
    later(8000);
  }
  mo = new MutationObserver(() => {
    if (document.getElementById(BOX)) { opened = true; if (!ordered) clearTimeout(timer); return; }
    if (opened && !ordered) later(1500); // closed without ordering
  });
  mo.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('message', onMsg);
  stopActiveWatch = stop;
  // Their checkout never opened (a blocked script, an outage). Their buttons
  // stay disabled while it is still starting up, so a slow start is waited out.
  const check = () => {
    if (opened || document.getElementById(BOX)) return;
    if (document.querySelector('.sr-headless-checkout[disabled]') && ++tries < 6) { never = setTimeout(check, 10000); return; }
    stop();
    restoreCart();
  };
  never = setTimeout(check, 30000);
}

// Any page: a cart still set aside by a Buy now (an order was placed and the
// page left, the tab was closed mid-checkout, or the native checkout was
// abandoned with Back) goes back in.
(function putBackAfterBuyNow() {
  const run = () => {
    const st = readStash();
    if (!st) return;
    const mine = ownsStash(st);
    // Another tab's Buy now checkout may still be open, and putting the cart
    // back under it would let that order's clear-cart wipe it.
    if (!mine && Date.now() - (st.beat || st.at) < STALE_MS) return;
    // Fastrr resuming a payment (a UPI or card redirect back here) or opening
    // an abandoned-cart link: watch that checkout instead.
    if (/[?&#](fastrr_status|fastrr_transaction_id|cart-resume-id)=/.test(location.search + location.hash)
      || document.getElementById('fastrr-main-container')) {
      if (mine) { startBeat(); watchSoloCheckout(); }
      return;
    }
    restoreCart();
  };
  run();
  window.addEventListener('pageshow', (e) => { if (e.persisted) run(); });
})();

const FLY_MS = 850; // keep in sync with flyToCart timeline duration
// One Buy now at a time: a double tap on the theme's own Buy it now submitted
// twice and bought two (offline simulation, 2026-09-29). Fastrr's hand-off and
// the sticky bar have their own guards.
let buyNowBusy = false;
window.addEventListener('pageshow', (e) => { if (e.persisted) buyNowBusy = false; });
async function addToCart(items, btn, opts) {
  const buyNow = !!(opts && opts.redirect);
  if (buyNow && buyNowBusy) return;
  btn?.setAttribute('aria-busy', 'true');
  const t0 = performance.now();
  if (!buyNow) flyToCart(btn); // skip the fly animation when leaving the page
  hideAddError(btn);
  try {
    // Buy now: the cart becomes just this product and the gift it earns (the
    // rest is set aside, see soloCart). In India the checkout is Fastrr, and
    // /checkout is the NATIVE Shopify one, so sending someone there loses COD
    // and the prepaid discount: open the drawer and click Fastrr's own checkout
    // button. If it never hydrates, the drawer simply stays open with that
    // button under the shopper's thumb: one tap, not a dead end.
    if (buyNow) {
      buyNowBusy = true;
      const res = await soloCart(items);
      if (res.error) { buyNowBusy = false; console.warn('Buy now failed', res.data || res.status); showAddError(btn, res.error); return; }
      const sr = await waitForFastrr();
      if (sr) {
        watchSoloCheckout();
        document.dispatchEvent(new CustomEvent('cart:open'));
        setTimeout(() => { buyNowBusy = false; sr.click(); }, 320);
        return;
      }
      // Fastrr never came up. The native /checkout is closed to shoppers
      // (merchant, 2026-10-11), so hand over to the checkout guard
      // (snippets/checkout-guard.liquid): it keeps waiting for Fastrr, then
      // offers Try again / WhatsApp. The set-aside cart goes back on the usual
      // never-opened timer.
      buyNowBusy = false;
      watchSoloCheckout();
      if (window.taabaaCheckout) window.taabaaCheckout();
      return;
    }
    const r = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, sections: 'cart-drawer,header' })
    });
    const res = await cartJson(r);
    if (res.error) { console.warn('Add to cart failed', res.data || r.status); showAddError(btn, res.error); return; }
    const data = res.data;
    window.refreshCartSections?.(data.sections);
    flashAdded(btn);
    // Open the cart once the flying image has landed — but never add dead time
    // if the request itself already took longer than the fly animation.
    const wait = Math.max(0, FLY_MS - (performance.now() - t0));
    setTimeout(() => document.dispatchEvent(new CustomEvent('cart:open')), wait);
  } catch (err) {
    if (buyNow) buyNowBusy = false;
    console.warn('Add to cart failed', err);
    showAddError(btn, ADD_FAIL);
  } finally { btn?.removeAttribute('aria-busy'); }
}
// Force an explicit selection on the PDP before adding to cart.
function flashSelect(el) {
  try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
  el.classList.add('ring-2', 'ring-red', 'ring-offset-2', 'rounded-xl');
  let note = el.querySelector('[data-select-note]');
  if (!note) {
    note = document.createElement('p');
    note.setAttribute('data-select-note', '');
    note.className = 'mt-2 font-sans text-small text-red';
    el.appendChild(note);
  }
  note.textContent = el.hasAttribute('data-colour-pick') ? 'Please pick your colours first.' : 'Please choose an option first.';
  note.style.display = '';
  clearTimeout(el.__selT);
  el.__selT = setTimeout(() => { el.classList.remove('ring-2', 'ring-red', 'ring-offset-2'); }, 1800);
}
// Returns true (and nudges the user) when a required PDP selection is missing.
function pdpGuard() {
  const pick = document.querySelector('[data-colour-pick]');
  if (pick) {
    const hidden = pick.querySelector('[data-pick-input]');
    if (!hidden || !hidden.value.trim()) { flashSelect(pick); return true; }
    return false;
  }
  const vp = document.querySelector('product-form variant-picker[data-has-options="true"]');
  if (vp && vp.dataset.touched !== 'true') { flashSelect(vp); return true; }
  return false;
}
// The line a PDP form is currently describing: chosen variant, chosen quantity,
// and every properties[...] field the customer filled in (colours, AC brand and
// model, phone model, custom colour request).
//
// This is the ONE place that reads the form. The sticky bar used to build its
// own {id, quantity: 1} payload, which silently dropped every property and the
// quantity with them: a customer picked a colour, scrolled, added from the
// sticky bar, and the order arrived with no colour on it. Anything that adds
// the PDP product must come through here.
// Colour name -> its photo, read straight off the swatch buttons. The cart
// drawer looks this up from the metafield itself, but a notification email
// cannot read metafields, so the URL rides along on the line as a hidden
// `_colour_image` property (underscore = never shown to the customer).
function colourImages() {
  const map = {};
  document.querySelectorAll('[data-pick-swatch]').forEach((sw) => {
    const n = (sw.getAttribute('data-name') || '').trim();
    const i = sw.getAttribute('data-image');
    if (n && i) map[n] = i;
  });
  return map;
}
function colourKeyOf(props) {
  return Object.keys(props).find((k) => /^colours?$|^colors?$/i.test(k));
}

function itemsFromForm(form) {
  const idEl = form.querySelector('[name="id"]');
  if (!idEl) return [];
  const id = Number(idEl.value);
  const raw = Number(form.querySelector('[name="quantity"]')?.value || 1);
  const qty = raw > 0 ? raw : 1;
  const props = {};
  form.querySelectorAll('[name^="properties["]').forEach((el) => {
    const m = el.name.match(/^properties\[(.+)\]$/);
    if (m && String(el.value).trim()) props[m[1]] = String(el.value).trim();
  });

  // ONE LINE PER COLOUR.
  //
  // The picker stores every choice in a single comma-joined properties[Colours],
  // so buying two of something in two colours used to arrive as one line reading
  // "2 x Clutch, Colours: Navy Blue, Baby Pink" — which of the two is which is
  // then anybody's guess on the order, the packing slip and the customer's
  // email. Split it so each colour is its own line with its own quantity.
  //
  // Only when the product takes ONE colour per unit (data-count, from
  // custom.colours_per_unit) AND the customer named a colour for every unit.
  // A product whose single unit uses several colours must stay one line, and a
  // partial pick (3 ordered, 2 colours named) is left alone rather than guessed
  // at, so the quantity can never come out different from what was asked for.
  const pick = document.querySelector('[data-colour-pick]');
  const perUnit = pick ? (parseInt(pick.getAttribute('data-count'), 10) || 1) : 1;
  const chosen = String(props.Colours || '').split(',').map((s) => s.trim()).filter(Boolean);
  const imgs = colourImages();
  const ckey = colourKeyOf(props);
  if (perUnit === 1 && chosen.length > 1 && chosen.length === qty) {
    const counts = new Map();
    chosen.forEach((c) => counts.set(c, (counts.get(c) || 0) + 1));
    return [...counts].map(([colour, n]) => {
      const p = Object.assign({}, props, { [ckey || 'Colours']: colour });
      // Each split line gets ITS colour's photo, not the whole pick's.
      if (imgs[colour]) p._colour_image = imgs[colour];
      return { id, quantity: n, properties: p };
    });
  }

  // One line: the first named colour is the one the photo represents.
  if (ckey && chosen.length && imgs[chosen[0]]) props._colour_image = imgs[chosen[0]];

  const item = { id, quantity: qty };
  if (Object.keys(props).length) item.properties = props;
  return [item];
}

// Quick-add buttons. Two different jobs share this hook: the sticky bar on a
// product page, which must add THIS product with everything the customer chose,
// and the Add buttons on product rails (reels, best sellers), which add another
// product outright and have no form behind them.
document.addEventListener('click', (e) => {
  const qa = e.target.closest('[data-quick-add][data-variant-id]');
  if (!qa || qa.disabled) return;
  e.preventDefault();
  if (qa.closest('[data-sticky-atc]')) {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      if (pdpGuard()) return;              // force a colour / variant choice
      if (!form.reportValidity()) return;  // enforce required fields, e.g. phone model
      const items = itemsFromForm(form);
      if (items.length) { addToCart(items, qa); return; }
    }
  }
  addToCart([{ id: Number(qa.dataset.variantId), quantity: 1 }], qa);
});

// Product-card option chips: switch the quick-add variant by option-1 value
const CARD_ON = ['bg-forest', 'text-cream', 'border-forest'];
const CARD_OFF = ['border-foreground/30', 'text-foreground/75'];
document.addEventListener('click', (e) => {
  const opt = e.target.closest('[data-card-opt]');
  if (!opt) return;
  const card = opt.closest('[data-card]');
  const json = card && card.querySelector('[data-card-variants]');
  const qa = card && card.querySelector('[data-quick-add]');
  if (!json || !qa) return;
  let variants;
  try { variants = JSON.parse(json.textContent); } catch (_) { return; }
  const matches = variants.filter((v) => v.options[0] === opt.dataset.value);
  const v = matches.find((m) => m.available) || matches[0];
  if (!v) return;
  card.querySelectorAll('[data-card-opt]').forEach((b) => {
    const on = b === opt;
    if (b.hasAttribute('data-swatch')) {
      b.classList.toggle('border-forest', on);
      b.classList.toggle('ring-2', on);
      b.classList.toggle('ring-forest/40', on);
      b.classList.toggle('border-foreground/20', !on);
    } else {
      b.classList.toggle(CARD_ON[0], on); b.classList.toggle(CARD_ON[1], on); b.classList.toggle(CARD_ON[2], on);
      b.classList.toggle(CARD_OFF[0], !on); b.classList.toggle(CARD_OFF[1], !on);
    }
  });
  qa.dataset.variantId = v.id;
  qa.disabled = !v.available;
  const lab = qa.querySelector('[data-qa-label]');
  if (lab) lab.textContent = v.available ? 'Add to cart' : 'Sold out';
  const img = card.querySelector('a img');
  if (img && v.featured_image && v.featured_image.src) img.src = v.featured_image.src;
});
// Full product form
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      if (pdpGuard()) return; // force variant/colour selection before adding
      if (!this.form.reportValidity()) return; // enforce required fields (e.g. phone model)
      const items = itemsFromForm(this.form);
      if (!items.length) return;
      const submitter = e.submitter;
      const buyNow = !!(submitter && submitter.hasAttribute('data-buy-now'));
      addToCart(items, submitter || this.form.querySelector('[type="submit"]'),
        buyNow ? { redirect: window.Shopify.routes.root + 'checkout' } : undefined);
    });
  }
}
customElements.define('product-form', ProductForm);

// Buy now must never be looser than Add to cart. Add to cart is already gated
// correctly everywhere (colour picker, variant picker, sold out), so mirroring
// its disabled state keeps one source of truth instead of a second copy of the
// rules that can drift.
(function mirrorBuyNow() {
  const atc = document.querySelector('[data-atc]');
  if (!atc) return;
  const bn = document.querySelector('[data-buy-now]');
  // Fastrr's branded BUY NOW is a third-party button with its own inline
  // onclick, so it cannot simply be `disabled`. Locking the mount instead
  // covers the button whenever their script gets round to injecting it.
  const sr = document.querySelector('[data-buy-row] .shiprocket-headless');
  const sync = () => {
    const off = atc.disabled;
    if (bn) bn.disabled = off;
    if (sr) sr.classList.toggle('sr-locked', off);
  };
  new MutationObserver(sync).observe(atc, { attributes: true, attributeFilter: ['disabled'] });
  sync();
  // pointer-events in sr-checkout.liquid is the real lock; this is the backstop
  // for the day Shiprocket's dashboard styles start overriding it. Capture
  // phase, because their handler sits inline on the button itself.
  if (sr) {
    sr.addEventListener('click', (e) => {
      if (!atc.disabled) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      pdpGuard();
    }, true);
  }
})();
// Every PDP mounts Fastrr as data-type="cart" (see the mount in
// main-product.liquid). Their product handler never touches the cart, so the
// free gift the order earns was never on it, and it drops properties unless the
// shop is on a hardcoded allowlist we are not on (order #1048: an AC cover with
// no brand and no model). Their cart handler reads /cart.js and keeps
// `customAttributes: {...line.properties}`.
//
// So the cart has to hold this product, and only this product, with the gift
// it earns before their handler runs (soloCart sets the rest aside and
// watchSoloCheckout puts it back). The first click is spent on that, then
// replayed so their own handler takes over untouched.
const fastrrPdp = (function fastrrPdpCartHandoff() {
  const mount = document.querySelector('[data-sr-pdp-cart]');
  const form = document.querySelector('form[data-product-form]');
  if (!mount || !form) return null;
  let replaying = false;
  let busy = false;
  // `btn` is Fastrr's own button; `from` is the button the shopper pressed
  // (theirs, or the sticky bar's), which shows the busy state and any error.
  function start(btn, from) {
    // An impatient second click would otherwise put the item in twice.
    if (busy) return;
    if (pdpGuard()) return;
    // The brand and model inputs carry `required`, and Fastrr's button is not a
    // submit button, so nothing else would enforce them.
    if (!form.reportValidity()) return;
    const items = itemsFromForm(form);
    if (!items.length) return;
    busy = true;
    btn.setAttribute('aria-busy', 'true');
    from.setAttribute('aria-busy', 'true');
    hideAddError(from);
    const done = () => { busy = false; btn.removeAttribute('aria-busy'); from.removeAttribute('aria-busy'); };
    (async () => {
      const res = await soloCart(items);
      if (res.error) { console.warn('Buy now failed', res.data || res.status); showAddError(from, res.error); return false; }
      return true;
    })()
      .then((go) => {
        done();
        if (!go) return;
        // Their script may have rebuilt the button while we waited.
        const target = btn.isConnected ? btn : mount.querySelector('button');
        if (!target) { restoreCart(); return; }
        watchSoloCheckout();
        // Reset even if their button swallows the click (disabled, or Add to
        // cart went off meanwhile), or the next tap would skip this hand-off
        // and check out the whole cart.
        replaying = true;
        try { target.click(); } finally { replaying = false; }
      })
      .catch(() => { done(); showAddError(from, ADD_FAIL); });
  }
  mount.addEventListener('click', (e) => {
    if (replaying) { replaying = false; return; }
    const btn = e.target.closest('button');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    start(btn, form);
  }, true);
  return {
    // Fastrr is showing here (India, and their script loaded) with its button in.
    ready: () => !!mount.querySelector('button') && getComputedStyle(mount).display !== 'none',
    start: (from) => { const b = mount.querySelector('button'); if (b) start(b, from); },
  };
})();

// The sticky bar's Buy now (snippets/sticky-atc.liquid). Exactly the buy box's
// Buy now: Fastrr's checkout through the hand-off above where Fastrr is
// showing, the theme's own Buy it now everywhere else, and in both cases only
// this product with the gift it earns.
document.addEventListener('click', (e) => {
  const sb = e.target.closest('[data-sticky-buy]');
  if (!sb || sb.disabled || sb.getAttribute('aria-busy') === 'true') return;
  e.preventDefault();
  const form = document.querySelector('form[data-product-form]');
  if (!form) return;
  // Add to cart is the one judge of "ready to buy". While it is off, a colour
  // or option is still to be chosen: take the shopper to it.
  const atc = document.querySelector('[data-atc]');
  if (atc && atc.disabled) { pdpGuard(); return; }
  if (pdpGuard()) return;
  if (!form.reportValidity()) return;
  if (fastrrPdp && fastrrPdp.ready()) { fastrrPdp.start(sb); return; }
  const items = itemsFromForm(form);
  if (items.length) addToCart(items, sb, { redirect: window.Shopify.routes.root + 'checkout' });
});

// The cart's own checkout buttons (drawer and /cart page; Fastrr's in India, the
// native ones elsewhere) hold a click while the gift engine is still working on
// the cart, then let it through. Adding something and tapping checkout within a
// second or so used to leave before the gift went in. When the engine is quiet,
// which is nearly always, the click passes straight through untouched.
(function holdCheckoutForGift() {
  let replaying = false;
  document.addEventListener('click', (e) => {
    if (replaying || e.defaultPrevented) return;
    if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // new-tab clicks
    // Fastrr's cart buttons only: the native checkout buttons never get this far
    // (snippets/checkout-guard.liquid stops them first, 2026-10-11).
    const el = e.target.closest('.shiprocket-headless[data-type="cart"] button');
    if (!el || el.closest('[data-sr-pdp-cart]')) return; // the PDP button has its own handoff above
    if (typeof window.taabaaGiftsPending !== 'function' || !window.taabaaGiftsPending()) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    if (el.getAttribute('aria-busy') === 'true') return;
    const scope = el.closest('[data-cart-page]') ? '[data-cart-page]' : '.cart-drawer__foot';
    el.setAttribute('aria-busy', 'true');
    giftsReady().then(async () => {
      el.removeAttribute('aria-busy');
      // The engine re-renders the drawer (and the /cart page) when it changes
      // the cart, which replaces these buttons; find the new one.
      let target = el;
      if (!el.isConnected) {
        target = await waitForFastrr(2200, scope);
      }
      if (!target) return; // left open with the button under the shopper's thumb
      replaying = true;
      try { target.click(); } finally { replaying = false; }
    });
  }, true);
})();

// Variant picker: update hidden id + price + availability from variant <option>/<input>
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent);
    this.addEventListener('change', () => { this.dataset.touched = 'true'; this.update(); });
    // Reconcile with whatever is already selected. Arriving from a collection
    // colour card (?colour=Dark Green) the inline picker script checks that
    // radio while the page is still parsing, which is BEFORE this file has
    // defined the element, so the change event it fired had no listener yet.
    // The page then showed Dark Green while the hidden id still held the first
    // colour, and Fastrr was handed Off White. Reading the DOM on upgrade makes
    // the sync independent of which script ran first.
    this.update();
  }
  update() {
    // Every option the shopper still has to choose. Colour-as-variant renders
    // its radios UNCHECKED on purpose, so an unanswered group is the normal
    // state of a freshly loaded PDP and not an error: the buy buttons stay off
    // until it is answered. Without this, changing a second option (the tonnage
    // on an AC cover) matched some arbitrary variant of the unpicked colour and
    // re-enabled Add to cart behind the colour gate.
    const groups = Array.from(this.querySelectorAll('[data-option-group]'));
    let missing = false;
    let chosen;
    if (groups.length) {
      chosen = [];
      groups.forEach((g) => {
        const sel = g.querySelector('select');
        if (sel) { chosen.push(sel.value); return; }
        const r = g.querySelector('input[type="radio"]:checked');
        if (r) chosen.push(r.value); else missing = true;
      });
    } else {
      chosen = Array.from(this.querySelectorAll('select, input:checked')).map((el) => el.value);
    }
    const match = this.variants.find((v) => chosen.every((opt) => v.options.includes(opt)));
    const root = this.closest('[data-product-root]') || document;
    const idInput = root.querySelector('[name="id"]');
    const priceEl = root.querySelector('[data-price]');
    const atc = root.querySelector('[data-atc]');
    const sticky = document.querySelector('[data-sticky-atc] [data-quick-add]');
    // A colour picker on the page outranks anything decided here. Changing a
    // second option (the tonnage on an AC cover, the pair count on a payal)
    // calls update(), and without this the buy buttons came back on while the
    // colour was still unchosen.
    const pick = document.querySelector('[data-colour-pick]');
    const unchosen = !!pick && !String(pick.querySelector('[data-pick-input]')?.value || '').trim();
    if (missing) {
      if (atc) atc.disabled = true;
      if (sticky) sticky.disabled = true;
      return;
    }
    if (!match) { if (atc) { atc.disabled = true; if (sticky) sticky.disabled = true; labelEl(atc).textContent = 'Unavailable'; } return; }
    if (idInput) idInput.value = match.id;
    this.dispatchEvent(new CustomEvent('pdp:variant', { bubbles: true, detail: { id: match.id } }));
    // Once the shopper has chosen (or arrived on a ?variant= link), keep the
    // address on that variant, so a refresh or a shared link opens on the same
    // colour. A plain visit is left alone until they pick something.
    if (!unchosen && (this.dataset.touched === 'true' || /[?&]variant=/.test(window.location.search))) {
      try {
        const u = new URL(window.location.href);
        // Colour-as-variant: ?variant= now names the colour, and a leftover
        // ?colour= (from a collection swatch) would win over it on refresh.
        const staleColour = !!(pick && pick.hasAttribute('data-variant-mode') && u.searchParams.has('colour'));
        if (u.searchParams.get('variant') !== String(match.id) || staleColour) {
          u.searchParams.set('variant', match.id);
          if (staleColour) u.searchParams.delete('colour');
          window.history.replaceState(window.history.state, '', u.toString());
        }
      } catch (e) { /* old browser: the address just does not follow */ }
    }
    const pricesEl = root.querySelector('[data-variant-prices]');
    const pricesMap = pricesEl ? JSON.parse(pricesEl.textContent) : {};
    if (priceEl && pricesMap[match.id]) priceEl.innerHTML = pricesMap[match.id];
    if (sticky) { sticky.dataset.variantId = match.id; sticky.disabled = unchosen || !match.available; }
    const stickyPrice = document.querySelector('[data-sticky-atc] [data-price]');
    if (stickyPrice && pricesMap[match.id]) stickyPrice.innerHTML = pricesMap[match.id];
    if (atc) { atc.disabled = unchosen || !match.available; labelEl(atc).textContent = match.available ? 'Add to cart' : 'Sold out'; }
  }
}
customElements.define('variant-picker', VariantPicker);

// Lazy-load product recommendations + reveal sticky ATC after hero scrolls away
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.querySelector('.product-grid')) return;
    fetch(this.dataset.url).then((r) => r.text()).then((html) => {
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations');
      if (el) {
        this.innerHTML = el.innerHTML;
        this.querySelectorAll('[data-reveal], [data-reveal-stagger]').forEach((node) => {
          node.style.transitionDelay = '';
          node.classList.add('is-visible');
        });
      }
    }).catch(() => {});
  }
}
customElements.define('product-recommendations', ProductRecommendations);
// WhatsApp bubble vs the buy box. On phones the bubble sat on the right end of
// Add to cart, the pincode Check button and "Use my current location". It now
// fades out (html.pdp-buy-in-view, CSS in sections/cart-drawer.liquid) only
// while part of the buy box is actually inside the bubble's own corner of the
// screen, and is back as soon as that has scrolled past. The observer's root is
// shrunk to that corner with negative rootMargins; it is re-armed when the
// sticky bar lifts the bubble and on resize.
// env(safe-area-inset-bottom) in px (the iPhone home bar), which the sticky
// bar and the bubble's lift both add.
function safeBottom() {
  try {
    const p = document.createElement('div');
    p.style.cssText = 'position:fixed;bottom:0;left:0;width:0;height:env(safe-area-inset-bottom,0px);visibility:hidden;pointer-events:none';
    document.body.appendChild(p);
    const h = p.getBoundingClientRect().height || 0;
    p.remove();
    return h;
  } catch (e) { return 0; }
}
const armWaCorner = (function () {
  const wa = document.querySelector('.whatsapp-float');
  const root = document.querySelector('[data-product-root]');
  if (!wa || !root || !('IntersectionObserver' in window)) return () => {};
  const targets = Array.from(root.querySelectorAll('product-form, [data-coupons], [data-offers], [data-pincode], .pdp-hamper-slot'));
  if (!targets.length) return () => {};
  const html = document.documentElement;
  const seen = new Set();
  let io = null;
  let lastKey = '';
  return function arm() {
    const lifted = html.classList.contains('sticky-atc-on');
    const W = window.innerWidth, H = window.innerHeight;
    const key = [lifted, W, H].join('|');
    if (key === lastKey) return;
    lastKey = key;
    // Bubble: 54px, 18px from the right and bottom, raised 5.75rem (92px) over
    // the sticky bar. The band is the bubble plus 12px of air; while the bar is
    // up, the strip under the bar is left out (nothing there is visible anyway).
    const bar = lifted ? 92 + safeBottom() : 0;
    const top = Math.max(0, H - (18 + 54 + 12 + bar));
    const left = Math.max(0, W - (18 + 54 + 12));
    if (io) io.disconnect();
    seen.clear();
    io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) seen.add(e.target); else seen.delete(e.target); });
      html.classList.toggle('pdp-buy-in-view', seen.size > 0);
    }, { rootMargin: `${-top}px 0px ${-bar}px ${-left}px`, threshold: 0 });
    targets.forEach((t) => io.observe(t));
  };
})();
armWaCorner();
{
  let waT = null;
  window.addEventListener('resize', () => { clearTimeout(waT); waT = setTimeout(armWaCorner, 200); });
}

const stickyAtc = document.querySelector('[data-sticky-atc]');
const atcBtn = document.querySelector('[data-atc]');
if (stickyAtc && atcBtn) {
  // Up only once Add to cart has scrolled off the TOP. On a phone the button
  // starts below the fold, and a bar shown on first load sat over the buy box's
  // price. The huge bottom margin makes "below the screen" count as in view, so
  // even a jump straight past the button (a reviews link) crosses the top edge.
  const io = new IntersectionObserver(([e]) => {
    const past = !e.isIntersecting && e.boundingClientRect.top < 0;
    stickyAtc.hidden = !past;
    document.documentElement.classList.toggle('sticky-atc-on', past);
    armWaCorner(); // the bubble moved up or down with the bar
  }, { threshold: 0, rootMargin: '0px 0px 100000px 0px' });
  io.observe(atcBtn);
}
// Quantity steppers
document.addEventListener('click', (e) => {
  const inc = e.target.closest('[data-q-inc]'); const dec = e.target.closest('[data-q-dec]');
  if (!inc && !dec) return;
  const input = (inc || dec).parentElement.querySelector('input[name="quantity"]');
  input.value = Math.max(1, parseInt(input.value, 10) + (inc ? 1 : -1));
  input.dispatchEvent(new Event('input', { bubbles: true })); // let the colour picker rescale its max
});
